import type {
  AcuityApproversResponse,
  AcuityHistoryResponse,
  AcuityResolutionPayload,
  AcuityResolutionResponse,
  PostAcuityScoreResponse,
  UUID,
  PostAcuityScorePayload,
  ReasoningOptionsResponse,
  CurrentAcuityScoreResponse,
} from "@/core/types";
import type { ApiRequest, ApiRequestWith } from "@/api/types";
import { getGroupAuthType } from "@/utils/authToken";
import { GROUP_AUTH_HEADER_NAME } from "@/api/constants";
import { httpAuth2 } from "@/api/auth";

type AcuityRequestsTyped = {
  get_acuity_history: ApiRequestWith<AcuityHistoryResponse, { urlParams: { patientId: UUID } }>;
  get_approvers: ApiRequest<AcuityApproversResponse>;
  get_current_acuity_score: ApiRequestWith<
    CurrentAcuityScoreResponse,
    { urlParams: { patientId: UUID } }
  >;
  get_reasoning_options: ApiRequest<ReasoningOptionsResponse>;
  post_acuity_resolution: ApiRequestWith<
    AcuityResolutionResponse,
    { body: AcuityResolutionPayload; urlParams: { patientId: UUID } }
  >;
  post_acuity_score: ApiRequestWith<
    PostAcuityScoreResponse,
    { body: PostAcuityScorePayload; urlParams: { patientId: UUID } }
  >;
};

export const acuityRequestsTyped: AcuityRequestsTyped = {
  get_acuity_history: function ({ context, urlParams }) {
    return httpAuth2
      .get(`/v1/acuity_score/patient/${urlParams.patientId}/history`, {
        context: { preventAllErrorToast: true, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<AcuityHistoryResponse>();
  },

  get_approvers: function ({ context } = {}) {
    return httpAuth2
      .get("/v1/acuity_score/approvers", {
        context: { preventAllErrorToast: true, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<AcuityApproversResponse>();
  },

  get_current_acuity_score: function ({ context, urlParams }) {
    return httpAuth2
      .get(`/v1/acuity_score/patient/${urlParams.patientId}/current`, {
        context: { preventAllErrorToast: true, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<CurrentAcuityScoreResponse>();
  },

  get_reasoning_options: function ({ context } = {}) {
    return httpAuth2
      .get("/v1/acuity_score/reasoning_options", {
        context: { preventAllErrorToast: true, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<ReasoningOptionsResponse>();
  },

  post_acuity_resolution: function ({ body, context, urlParams }) {
    return httpAuth2
      .post(`/v1/acuity_score/patient/${urlParams.patientId}/resolution`, {
        context: { preventAllErrorToast: true, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: body,
      })
      .json<AcuityResolutionResponse>();
  },

  post_acuity_score: function ({ body, context, urlParams }) {
    return httpAuth2
      .post(`/v1/acuity_score/patient/${urlParams.patientId}/request`, {
        context: { preventAllErrorToast: true, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: body,
      })
      .json<PostAcuityScoreResponse>();
  },
};
